import { getLastKnownAuthUser } from "./authService";
import { registerMaterialentnahmeBackgroundSync } from "./serviceWorkerService";
import { getAllLocalWithdrawals, getLocalWithdrawal, saveLocalWithdrawal } from "../storage/offlineDatabase";
import type { LocalWithdrawalRecord } from "../types/offlineWithdrawal";
import type { WithdrawalHistoryItem } from "../types/withdrawalHistory";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";

export type PaperHandoverConfirmResult = {
  status: "completed" | "completed_pending_sync";
  confirmedClientAt: string;
  confirmedServerAt: string | null;
};

class PaperHandoverRequestError extends Error {
  readonly retryable: boolean;

  constructor(message: string, retryable: boolean) {
    super(message);
    this.name = "PaperHandoverRequestError";
    this.retryable = retryable;
  }
}

function eventId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return `handover-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function skeletonRecord(item: WithdrawalHistoryItem, userId: number, username: string): LocalWithdrawalRecord {
  const now = new Date().toISOString();
  return {
    client_uuid: item.uuid,
    record_type: "completed",
    owner_user_id: userId,
    owner_username: username,
    local_withdrawal_number: item.withdrawal_number,
    data: {
      client_uuid: item.uuid,
      withdrawal_date: item.withdrawal_date,
      material_number: item.material_number,
      material_text: item.material_text,
      material_type: item.material_type,
      building: "",
      floor: "",
      cabinet: "",
      compartment: "",
      sub_compartment: "",
      quantity_removed: item.quantity_removed,
      current_stock: item.current_stock,
      accounting_system: item.accounting_system,
      accounting_subarea: item.accounting_subarea,
      accounting_manufacturer: item.accounting_manufacturer,
      accounting_number: item.accounting_number,
      order_info: null,
      remark: null,
      persons: item.persons.map((name) => ({ name })),
    },
    pdf_blob: null,
    pdf_filename: null,
    pdf_preview_blob: null,
    pdf_preview_filename: null,
    label_blob: null,
    label_filename: null,
    sync_status: "synced",
    server_withdrawal_id: item.id,
    server_withdrawal_number: item.withdrawal_number,
    base_version: item.current_version,
    created_at: item.created_at || now,
    updated_at: now,
    last_sync_attempt_at: null,
    sync_attempt_count: 0,
    next_sync_attempt_at: null,
    synced_at: item.synced_at,
    last_error: null,
    deleted_at: item.deleted_at,
  };
}

async function persistPending(item: WithdrawalHistoryItem, confirmedAt: string, confirmEventId: string): Promise<void> {
  const user = getLastKnownAuthUser();
  if (!user) return;
  const existing = await getLocalWithdrawal(item.uuid).catch(() => null);
  const base = existing ?? skeletonRecord(item, user.id, user.username);
  await saveLocalWithdrawal({
    ...base,
    updated_at: new Date().toISOString(),
    paper_handover_status: "completed_pending_sync",
    paper_handover_confirmed_client_at: confirmedAt,
    paper_handover_confirmed_server_at: null,
    paper_handover_confirm_event_id: confirmEventId,
  });
  if (typeof window !== "undefined") window.dispatchEvent(new Event("materialentnahme:local-withdrawal-updated"));
  void registerMaterialentnahmeBackgroundSync();
}

async function markLocalCompleted(item: WithdrawalHistoryItem, confirmedAt: string, confirmedServerAt: string | null, confirmEventId: string): Promise<void> {
  const existing = await getLocalWithdrawal(item.uuid).catch(() => null);
  if (!existing) return;
  await saveLocalWithdrawal({
    ...existing,
    updated_at: new Date().toISOString(),
    paper_handover_status: "completed",
    paper_handover_confirmed_client_at: confirmedAt,
    paper_handover_confirmed_server_at: confirmedServerAt,
    paper_handover_confirm_event_id: confirmEventId,
  });
  if (typeof window !== "undefined") window.dispatchEvent(new Event("materialentnahme:local-withdrawal-updated"));
}

async function postConfirmation(withdrawalId: number, confirmEventId: string, confirmedAt: string): Promise<{ confirmed_server_at?: string | null }> {
  const response = await fetchWithTimeout(`/api/withdrawals/${withdrawalId}/paper-handover/confirm`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ client_event_id: confirmEventId, confirmed_at: confirmedAt }),
  }, 3000);
  if (!response.ok) {
    const payload = await response.json().catch(() => null) as { detail?: string } | null;
    const retryable = response.status >= 500 || response.status === 408 || response.status === 429;
    throw new PaperHandoverRequestError(
      payload?.detail || `Übergabe konnte nicht bestätigt werden (${response.status}).`,
      retryable,
    );
  }
  return response.json() as Promise<{ confirmed_server_at?: string | null }>;
}

export async function confirmPaperHandover(
  item: WithdrawalHistoryItem,
  options: { allowOfflinePending?: boolean } = {},
): Promise<PaperHandoverConfirmResult> {
  const allowOfflinePending = options.allowOfflinePending !== false;
  const confirmedAt = new Date().toISOString();
  const confirmEventId = eventId();
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    if (!allowOfflinePending) {
      throw new Error("Für die Admin-Quittierung eines anderen Benutzers ist eine Verbindung zum Server erforderlich.");
    }
    await persistPending(item, confirmedAt, confirmEventId);
    return { status: "completed_pending_sync", confirmedClientAt: confirmedAt, confirmedServerAt: null };
  }
  try {
    const response = await postConfirmation(item.id, confirmEventId, confirmedAt);
    await markLocalCompleted(item, confirmedAt, response.confirmed_server_at ?? null, confirmEventId);
    return { status: "completed", confirmedClientAt: confirmedAt, confirmedServerAt: response.confirmed_server_at ?? null };
  } catch (unknownError) {
    if (unknownError instanceof PaperHandoverRequestError && !unknownError.retryable) throw unknownError;
    if (!allowOfflinePending) throw unknownError;
    await persistPending(item, confirmedAt, confirmEventId);
    return { status: "completed_pending_sync", confirmedClientAt: confirmedAt, confirmedServerAt: null };
  }
}

export async function flushPendingPaperHandovers(): Promise<number> {
  const user = getLastKnownAuthUser();
  if (!user || (typeof navigator !== "undefined" && navigator.onLine === false)) return 0;
  const records = await getAllLocalWithdrawals().catch(() => []);
  let completed = 0;
  for (const record of records) {
    if (record.owner_user_id !== user.id || record.paper_handover_status !== "completed_pending_sync") continue;
    if (!record.server_withdrawal_id || !record.paper_handover_confirmed_client_at || !record.paper_handover_confirm_event_id) continue;
    try {
      const response = await postConfirmation(record.server_withdrawal_id, record.paper_handover_confirm_event_id, record.paper_handover_confirmed_client_at);
      await saveLocalWithdrawal({
        ...record,
        updated_at: new Date().toISOString(),
        paper_handover_status: "completed",
        paper_handover_confirmed_server_at: response.confirmed_server_at ?? new Date().toISOString(),
      });
      if (typeof window !== "undefined") window.dispatchEvent(new Event("materialentnahme:local-withdrawal-updated"));
      completed += 1;
    } catch (unknownError) {
      // Fachlich endgültige Antworten (z. B. kein aktiver Handover mehr) dürfen
      // nicht als ewiger Background-Retry liegen bleiben. Netzwerk-/5xx-Fehler
      // werden weiterhin über den bestehenden Background-Sync-Trigger versucht.
      if (unknownError instanceof PaperHandoverRequestError && !unknownError.retryable) {
        await saveLocalWithdrawal({
          ...record,
          updated_at: new Date().toISOString(),
          paper_handover_status: null,
          paper_handover_confirmed_client_at: null,
          paper_handover_confirmed_server_at: null,
          paper_handover_confirm_event_id: null,
        });
        if (typeof window !== "undefined") window.dispatchEvent(new Event("materialentnahme:local-withdrawal-updated"));
      } else {
        void registerMaterialentnahmeBackgroundSync();
      }
    }
  }
  if (completed > 0) window.dispatchEvent(new Event("materialentnahme:status-refresh"));
  return completed;
}
